import type { ProductView } from '../../../../../services/products/products.service';
import type { CategoryView } from '../../../../../services/categories/categories.service';
import { compareCategoryNamesForDisplay } from '../../../../../services/categories/category-order.util';
import { matchesSearchQuery, normalizeSearchText } from '../../../../../utils/semantic-search.util';
import { jerusalemDayAndMinutes, parseTimeToMinutes } from '../../../utils/happy-hour-window.util';

export function getHiddenCategoryIds(categories: CategoryView[], now: Date, isManager = true): Set<number> {
  const { minutes } = jerusalemDayAndMinutes(now);
  const hidden = new Set<number>();
  for (const category of categories) {
    let visible = true;
    if (category.visibilityStartTime != null || category.visibilityEndTime != null) {
      const start = parseTimeToMinutes(category.visibilityStartTime ?? '');
      const end = parseTimeToMinutes(category.visibilityEndTime ?? '');
      visible = start != null && end != null && start !== end
        && (start < end ? minutes >= start && minutes < end : minutes >= start || minutes < end);
    }
    if (!visible || (!isManager && category.managersOnly)) {
      for (const id of expandCategoryWithDescendants(category.id, categories)) hidden.add(id);
    }
  }
  return hidden;
}

// Catalog names are tenant data, so aliases stay generic: common Hebrew↔English food and drink
// terms. Keys must be normalized (no apostrophes/quotes).
const MENU_SYNONYMS: Record<string, string[]> = {
  קולה: ['cola', 'coca', 'קוקה'],
  קוקה: ['coca', 'cola', 'קולה'],
  cola: ['קולה', 'קוקה'],
  coca: ['קוקה', 'קולה'],
  בירה: ['beer'],
  beer: ['בירה'],
  קפה: ['coffee', 'אספרסו', 'espresso'],
  coffee: ['קפה'],
  מים: ['water'],
  water: ['מים'],
  יין: ['wine'],
  wine: ['יין'],
  שתייה: ['משקה', 'משקאות', 'drink'],
  משקה: ['שתייה', 'drink'],
  drink: ['שתייה', 'משקה'],
  ציפס: ['chips', 'fries'],
  chips: ['ציפס', 'fries'],
  המבורגר: ['burger', 'בורגר'],
  בורגר: ['burger', 'המבורגר'],
  burger: ['המבורגר', 'בורגר'],
  פיצה: ['pizza'],
  pizza: ['פיצה'],
  סלט: ['salad'],
  salad: ['סלט'],
  קינוח: ['dessert', 'מתוק'],
  dessert: ['קינוח'],
  עוף: ['chicken'],
  chicken: ['עוף'],
  בשר: ['meat', 'סטייק'],
  סטייק: ['steak', 'בשר'],
  steak: ['סטייק', 'בשר'],
  דג: ['fish', 'דגים'],
  fish: ['דג', 'דגים'],
  טבעוני: ['vegan'],
  vegan: ['טבעוני'],
  צמחוני: ['vegetarian'],
  ילדים: ['kids', 'ילד'],
  kids: ['ילדים'],
  שוט: ['shot', 'צייסר', 'chaser'],
  צייסר: ['chaser', 'שוט', 'shot'],
  chaser: ['צייסר', 'שוט', 'shot'],
};

export const ALL_CATEGORIES_LABEL = 'כל המוצרים';
export const UNCATEGORIZED_LABEL = 'ללא קטגוריה';
export const RECENT_RESULTS_LABEL = 'חיפוש אחרון';

// 'all' = grouped with separators; number = one category; 'uncategorized' = no category.
export type CategoryFilter = number | 'all' | 'uncategorized';

export type CategoryRailItem = {
  key: string;
  filter: CategoryFilter;
  label: string;
  happyHour: boolean;
};

export type MenuProductGroup = {
  key: string;
  categoryId: number | null;
  title: string | null;
  products: ProductView[];
};

// Architectural summary: Active categories by manager sortOrder, name (Hebrew locale) as tiebreak.
function sortCategoriesForMenu(categories: CategoryView[]): CategoryView[] {
  return [...categories]
    .filter((category) => category.isActive)
    .sort((a, b) => a.sortOrder - b.sortOrder || compareCategoryNamesForDisplay(a.name, b.name));
}

function sortByName(categories: CategoryView[]): CategoryView[] {
  return [...categories]
    .filter((category) => category.isActive)
    .sort((a, b) => compareCategoryNamesForDisplay(a.name, b.name));
}

function sortSiblingCategories(categories: CategoryView[]): CategoryView[] {
  return [...categories]
    .filter((category) => category.isActive)
    .sort((a, b) => a.sortOrder - b.sortOrder || compareCategoryNamesForDisplay(a.name, b.name));
}

// Architectural summary: Products by manager sortOrder, id as a stable tiebreak.
function sortProductsForMenu(products: ProductView[]): ProductView[] {
  return [...products].sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id);
}

// Architectural summary: Rail — "all" first, TOP-LEVEL active categories A→Z, "uncategorized" last.
// Sub-categories (parentCategoryPk !== null) are excluded here; they render in buildCategoryChildRail.
export function buildCategoryRail(categories: CategoryView[]): CategoryRailItem[] {
  const sorted = sortCategoriesForMenu(categories).filter((category) => category.parentCategoryPk === null);
  return [
    { key: 'all', filter: 'all', label: ALL_CATEGORIES_LABEL, happyHour: false },
    ...sorted.map((category) => ({
      key: `cat-${category.id}`,
      filter: category.id,
      label: category.name,
      happyHour: category.happyHour,
    })),
    { key: 'uncategorized', filter: 'uncategorized' as const, label: UNCATEGORIZED_LABEL, happyHour: false },
  ];
}

// Architectural summary: Direct active children of one category, A→Z — no "all"/"uncategorized" entries.
// Details: docs/function-library/features/category-hierarchy/category-hierarchy.md#pos-rails
export function buildCategoryChildRail(categories: CategoryView[], parentId: number): CategoryRailItem[] {
  const children = categories.filter((category) => category.parentCategoryPk === parentId);
  return sortSiblingCategories(children).map((category) => ({
    key: `cat-${category.id}`,
    filter: category.id,
    label: category.name,
    happyHour: category.happyHour,
  }));
}

// Architectural summary: Search filter applied before category grouping. Matches the product name
// or any ancestor category name, tolerating typos, Hebrew punctuation and common term aliases.
// A product-name hit always wins: matching a category too would let a short query like "אוכ"
// pull in every product under "אוכל" and bury the item actually being searched for. Category
// matching stays as a fallback, so "שתייה" still lists the drinks.
export function filterProductsBySearch(
  products: ProductView[],
  search: string,
  categories: CategoryView[] = [],
): ProductView[] {
  const trimmed = search.trim();
  if (!trimmed) {
    return products;
  }

  const categoryById = new Map(categories.map((category) => [category.id, category]));
  const categorySearchTextById = new Map<number, string>();
  for (const category of categories) {
    const parts: string[] = [];
    const visited = new Set<number>();
    let cursor: CategoryView | undefined = category;
    while (cursor && !visited.has(cursor.id)) {
      visited.add(cursor.id);
      parts.push(normalizeSearchText(cursor.name));
      cursor = cursor.parentCategoryPk === null ? undefined : categoryById.get(cursor.parentCategoryPk);
    }
    categorySearchTextById.set(category.id, parts.join(' '));
  }

  const nameMatches: ProductView[] = [];
  const categoryMatches: ProductView[] = [];
  for (const product of products) {
    if (matchesSearchQuery(trimmed, [normalizeSearchText(product.name)], MENU_SYNONYMS)) {
      nameMatches.push(product);
      continue;
    }
    const categorySearchText = product.categoryId === null
      ? undefined
      : categorySearchTextById.get(product.categoryId);
    if (categorySearchText && matchesSearchQuery(trimmed, [categorySearchText], MENU_SYNONYMS)) {
      categoryMatches.push(product);
    }
  }

  return nameMatches.length > 0 ? nameMatches : categoryMatches;
}

type CategoryParentLink = { id: number; parentCategoryPk: number | null };

// Architectural summary: Direct children ids grouped by parent id, for BFS traversal.
// Details: docs/function-library/features/category-hierarchy/category-hierarchy.md#descendant-expansion
export function buildChildrenByParent(categories: CategoryParentLink[]): Map<number, number[]> {
  const childrenByParent = new Map<number, number[]>();
  for (const category of categories) {
    if (category.parentCategoryPk === null) continue;
    const siblings = childrenByParent.get(category.parentCategoryPk) ?? [];
    siblings.push(category.id);
    childrenByParent.set(category.parentCategoryPk, siblings);
  }
  return childrenByParent;
}

// Architectural summary: A category id plus every id in its descendant subtree (children, grandchildren).
// Details: docs/function-library/features/category-hierarchy/category-hierarchy.md#descendant-expansion
export function expandCategoryWithDescendants(
  categoryId: number,
  allCategories: CategoryParentLink[],
): number[] {
  const childrenByParent = buildChildrenByParent(allCategories);
  const result: number[] = [categoryId];
  const queue: number[] = [categoryId];
  while (queue.length > 0) {
    const current = queue.shift() as number;
    for (const childId of childrenByParent.get(current) ?? []) {
      result.push(childId);
      queue.push(childId);
    }
  }
  return result;
}

// Architectural summary: One rail per ancestor level of the selection (root-first), so
// navigating deeper stacks rails instead of replacing the one above it, plus the set of
// ids on the active path (selection + every ancestor) for cross-rail highlighting.
// Details: docs/function-library/features/category-hierarchy/category-hierarchy.md#pos-rails
export function buildAncestorRails(
  categories: CategoryView[],
  selectedFilter: CategoryFilter,
): { rails: CategoryRailItem[][]; activeIds: Set<number> } {
  const activeIds = new Set<number>();
  if (typeof selectedFilter !== 'number') {
    return { rails: [], activeIds };
  }
  const byId = new Map(categories.map((category) => [category.id, category]));
  const ancestorChain: number[] = [];
  let cursor: number | null = selectedFilter;
  while (cursor !== null) {
    ancestorChain.unshift(cursor);
    activeIds.add(cursor);
    cursor = byId.get(cursor)?.parentCategoryPk ?? null;
  }
  const rails = ancestorChain
    .map((ancestorId) => buildCategoryChildRail(categories, ancestorId))
    .filter((rail) => rail.length > 0);
  return { rails, activeIds };
}

// Architectural summary: Product groups per rail filter; 'all' = per-category, uncategorized last.
export function buildMenuProductGroups(
  products: ProductView[],
  categories: CategoryView[],
  filter: CategoryFilter,
  search: string,
  recentResultIds: number[] = [],
): MenuProductGroup[] {
  const visible = filterProductsBySearch(products, search, categories);
  const sortedCategories = sortCategoriesForMenu(categories);
  const categoryIds = new Set(sortedCategories.map((category) => category.id));

  const isUncategorized = (product: ProductView): boolean =>
    product.categoryId == null || !categoryIds.has(product.categoryId);

  if (filter === 'uncategorized') {
    return [{ key: 'uncategorized', categoryId: null, title: null, products: sortProductsForMenu(visible.filter(isUncategorized)) }];
  }

  if (typeof filter === 'number') {
    const expandedIds = expandCategoryWithDescendants(filter, categories);
    // Leaf category (no descendants beyond itself): single untitled group, unchanged from prior behavior.
    if (expandedIds.length === 1) {
      return [
        {
          key: `cat-${filter}`,
          categoryId: filter,
          title: null,
          products: sortProductsForMenu(visible.filter((product) => product.categoryId === filter)),
        },
      ];
    }
    // Category with descendants: one titled group per descendant (including itself), same
    // separator treatment as the "all" view, so rolled-up sub-category products stay visually distinct.
    const byId = new Map(categories.map((category) => [category.id, category]));
    const activeByParent = new Map<number, CategoryView[]>();
    for (const category of sortSiblingCategories(categories)) {
      if (category.parentCategoryPk === null) continue;
      const siblings = activeByParent.get(category.parentCategoryPk) ?? [];
      siblings.push(category);
      activeByParent.set(category.parentCategoryPk, siblings);
    }

    const orderedIds: number[] = [];
    const pushSubtree = (categoryId: number): void => {
      orderedIds.push(categoryId);
      const children = activeByParent.get(categoryId) ?? [];
      for (const child of children) {
        pushSubtree(child.id);
      }
    };
    pushSubtree(filter);

    const groups: MenuProductGroup[] = [];
    for (const id of orderedIds) {
      const category = byId.get(id);
      if (!category) continue;
      const inCategory = sortProductsForMenu(visible.filter((product) => product.categoryId === id));
      if (inCategory.length > 0) {
        groups.push({ key: `cat-${id}`, categoryId: id, title: category.name, products: inCategory });
      }
    }
    return groups;
  }

  const groups: MenuProductGroup[] = [];
  // Frozen last-search snapshot shown as the first separator group; capture order, drop stale ids.
  if (recentResultIds.length > 0) {
    const byId = new Map(visible.map((product) => [product.id, product]));
    const recentProducts = recentResultIds
      .map((id) => byId.get(id))
      .filter((product): product is ProductView => product != null);
    if (recentProducts.length > 0) {
      groups.push({ key: 'recent', categoryId: null, title: RECENT_RESULTS_LABEL, products: recentProducts });
    }
  }
  for (const category of sortedCategories) {
    const inCategory = sortProductsForMenu(visible.filter((product) => product.categoryId === category.id));
    if (inCategory.length > 0) {
      groups.push({ key: `cat-${category.id}`, categoryId: category.id, title: category.name, products: inCategory });
    }
  }
  const uncategorized = sortProductsForMenu(visible.filter(isUncategorized));
  if (uncategorized.length > 0) {
    groups.push({ key: 'uncategorized', categoryId: null, title: UNCATEGORIZED_LABEL, products: uncategorized });
  }
  return groups;
}

// Summary: General-item tile slot among the menu groups; null while a search is active or when the
// tenant hid it from the menu (פעולות keeps its own entry either way).
export function resolveGeneralItemTileIndex(
  groups: MenuProductGroup[],
  search: string,
  hidden = false,
): number | null {
  if (hidden || search.trim().length > 0) {
    return null;
  }
  return groups[0]?.key === 'recent' ? 1 : 0;
}
